import { type VariantProps, cva } from 'class-variance-authority';

export const buttonStyles = cva('button', {
  variants: {
    intent: {
      primary_old: [
        'bg-blue-300 px-[20px] pt-[14px] pb-[13px] cursor-pointer',
        'text-white ',
        'uppercase font-bold font-trade-cn tracking-[1.4px] [text-shadow:0.01em_0_currentColor,-0.01em_0_currentColor]',
        'hover:bg-blue-300',
      ],
      secondary_old: [
        'bg-white px-[20px] pt-[14px] pb-[13px] cursor-pointer uppercase tracking-[1.4px]',
        'text-blue-700 [text-shadow:0.01em_0_currentColor,-0.01em_0_currentColor] font-bold font-trade-cn',
        'border-neutral-300 border',
      ],
      primary: [
        'cursor-pointer font-inter rounded-[0.375rem] flex items-center justify-center whitespace-nowrap gap-[0.375rem]',
      ],
      secondary: [
        'bg-white cursor-pointer font-inter border rounded-[0.375rem] flex items-center justify-center whitespace-nowrap gap-2',
      ],
      ghost: [
        'bg-transparent cursor-pointer font-medium font-inter flex items-center justify-center whitespace-nowrap gap-2',
      ],
      ghost_semibold: [
        'bg-transparent cursor-pointer text-blue-300 font-semibold font-inter flex items-center justify-center whitespace-nowrap gap-2',
      ],
      inline: [
        'bg-transparent font-medium font-inter font-base leading-[140%] flex items-center justify-center whitespace-nowrap gap-2 !px-0 !py-1',
      ],
      danger: [
        'bg-white cursor-pointer !font-medium font-inter border rounded-[0.375rem] flex items-center justify-center whitespace-nowrap gap-2',
        'border-red-500 text-red-500',
      ],
      ghost_danger: [
        'bg-transparent hover:bg-red-50 cursor-pointer text-red-500 font-medium font-inter flex items-center justify-center whitespace-nowrap gap-2',
      ],
    },
    size: {
      full: [
        'w-full px-[1.375rem] py-[0.563rem] text-base leading-[150%] [&>svg]:w-[1.125rem] [&>svg]:h-[1.125rem] max-h-[2.625rem]',
      ],
      full_responsive: [
        'w-fit px-[1.375rem] py-[0.563rem] text-base leading-[150%] [&>svg]:w-[1.125rem] [&>svg]:h-[1.125rem] max-h-[2.625rem]',
        'max-md:px-1.5 max-md:py-1 max-md:text-sm max-md:leading-[140%]',
      ],
      search: ['w-fit px-6 py-[0.563rem] text-base leading-[140%] max-h-10', 'max-md:text-sm'],
      iconFull: [
        'w-full px-[1.375rem] py-[0.563rem] text-base leading-[150%] [&>svg]:w-[1.125rem] [&>svg]:h-[1.125rem] max-h-[2.625rem]',
      ],
      medium: [
        'w-fit px-[1.375rem] py-[0.5rem] rounded-[0.25rem] text-sm h-[2.25rem] min-h-[2.25rem] [&>svg]:w-[1rem] leading-[140%] [&>svg]:h-[1rem]',
      ],
      ghost_medium: [
        'w-fit px-[1.375rem] py-[0.5rem] rounded-[0.25rem] font-semibold text-base h-[2.25rem] min-h-[2.25rem] [&>svg]:w-[1rem] leading-[140%] [&>svg]:h-[1rem]',
      ],
      ghost_small: [
        'w-fit px-[6px] py-[4px] rounded-[0.25rem] font-medium text-base h-[2.25rem] min-h-[2.25rem] [&>svg]:w-[1rem] leading-[140%] [&>svg]:h-[1rem]',
      ],
      medium_old: ['w-fit h-[44px]'],
      full_short: [
        'w-full px-[6px] py-1 text-base font-semibold leading-[140%] [&>svg]:w-[1.125rem] [&>svg]:h-[1.125rem] max-h-[2.625rem]',
      ],
      full_old: ['w-full h-[64px]'],
      small: ['font-normal text-sm px-[22px] py-[8px] leading-[140%] font-inter'],
      iconSquare: ['w-10 h-10 min-w-0 p-0 shrink-0'],
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    disabled: {
      false: null,
      true: ['!cursor-not-allowed'],
    },
  },
  compoundVariants: [
    {
      intent: 'secondary',
      disabled: true,
      class: ['bg-white border-[#495057] text-[#495057]'],
    },
    {
      intent: 'secondary',
      disabled: false,
      class: ['bg-white border-blue-300 text-blue-300 hover:bg-blue-50'],
    },
    {
      intent: 'primary',
      disabled: true,
      class: ['bg-[#6C757D] hover:bg-[#6C757D] text-grey-100'],
    },
    {
      intent: 'primary',
      disabled: false,
      class: ['bg-blue-300 hover:bg-blue-900 text-white'],
    },
    {
      intent: 'ghost',
      disabled: true,
      class: ['bg-transparent text-[#6C757D]'],
    },
    {
      intent: 'ghost',
      disabled: false,
      class: ['bg-transparent text-blue-300 hover:bg-blue-50'],
    },
    {
      intent: 'ghost_semibold',
      disabled: true,
      class: ['bg-transparent text-grey-700'],
    },
    {
      intent: 'ghost_semibold',
      disabled: false,
      class: ['bg-transparent text-blue-300 hover:bg-[#F2F6FC]'],
    },
    {
      intent: 'danger',
      disabled: false,
      class: ['hover:bg-red-50 hover:border-red-600 hover:text-red-600'],
    },
    {
      intent: 'danger',
      disabled: true,
      class: ['bg-gray-100 border-red-300 text-red-300 opacity-50'],
    },
    {
      intent: 'inline',
      disabled: false,
      class: ['text-blue-300 cursor-pointer'],
    },
    {
      intent: 'inline',
      disabled: true,
      class: ['text-neutral-700 opacity-50 cursor-not-allowed'],
    },
  ],
  defaultVariants: {
    disabled: false,
    intent: 'primary',
    size: 'medium',
    weight: 'medium',
  },
});

export type ButtonVariantsProps = VariantProps<typeof buttonStyles>;
